<template>
  <div v-if="roundFinished">
    <template v-if="votingRoundReport && roundFinished && votingRoundReport.publishedAt">
      <div class="p-3" v-for="(contestResult, index) in orderedContestResults" :key="contestResult.id">
        <hr class="mt-0" v-if="Number(index) > 0">
        <VotingRoundResult theme="light" :showTurnout="false" :contest-result="contestResult" :voting-round-report="votingRoundReport"
          :contest="contests.find(contest => contest.reference === contestResult.contestReference)" />
      </div>
    </template>
    <div v-else class="p-3 text-center">
      <p class="mb-0">{{ $t("js.voting_round_modal.not_published") }}</p>
    </div>
  </div>
  <div v-else-if="votingRound" class="d-grid" style="height: 90vh">
    <ElectionClientIframe :embedded-election-client-url="embeddedElectionClientUrl" :reference="reference"/>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import VotingRoundResult from "@/components/backend/live/VotingRoundResult.vue";
import {mapActions, mapState} from "pinia";
import {calculateState, label} from "@/entrypoints/shared/voting_rounds_helper";
import {AVClient} from "@assemblyvoting/js-client";
import {useSharedStore} from "@/entrypoints/stores/shared";
import {usePresentationStore} from "@/entrypoints/stores/presentation";
import {useVotingSessionStore} from "@/entrypoints/stores/voting_session";
import type { ConferenceVotingRound, E2E, PropType } from "@/types";
import {LoginsApiClient} from "@/lib/voting/loginsApiClient";
import ElectionClientIframe from "@/components/frontend/shared/ElectionClientIframe.vue";
import IndeterminateSpinner from "@/components/backend/live/IndeterminateSpinner.vue";

export default defineComponent({
  name: "BallotEmbedded",
  components: {IndeterminateSpinner, ElectionClientIframe, VotingRoundResult },
  props: {
    votingRound: {
      type: Object as PropType<ConferenceVotingRound>,
      required: true,
    },
  },
  computed: {
    ...mapState(useSharedStore, ["election"]),
    ...mapState(usePresentationStore, ["contests", "votingRoundReports"]),
    ...mapState(useVotingSessionStore, ["authenticateWith"]),
    reference() {
      return this.votingRound ? this.votingRound.reference : null;
    },
    embeddedElectionClientUrl() {
      return `${this.election.electionClientUrl}/${this.$i18n.locale}/${this.votingRound.reference}`;
    },
    roundFinished() {
      return this.votingRoundState === "finished";
    },
    votingRoundState() {
      return this.calculateState(this.votingRound, this.fetchVotingRoundReport(this.votingRound));
    },
    votingRoundReport() {
      return this.fetchVotingRoundReport(this.votingRound);
    },
    orderedContestResults() {
      const contestPositions = this.votingRound.contestPositions
      return this.votingRoundReport.contestResults.toSorted((a, b) =>
        contestPositions[a.contestReference] - contestPositions[b.contestReference]
      );
    }
  },
  methods: {
    label,
    calculateState,
    ...mapActions(usePresentationStore, ["setVotingRound"]),
    fetchVotingRoundReport(vr: ConferenceVotingRound) {
      return this.votingRoundReports.find((vrr: E2E.VotingRoundReport) => vrr.votingRoundReference === vr.reference);
    },
    closeModal() {
      const votingRoundToExpire = this.votingRound;
      const finished = this.roundFinished;
      this.setVotingRound(null);

      if (!finished) this.cancelVoting(votingRoundToExpire);
    },
    async cancelVoting(votingRoundToExpire: ConferenceVotingRound) {
      try {
        const client = new AVClient(this.election.boardUrl, this.election.dbbPublicKey);
        await client.initialize();
        if (this.authenticateWith.electionCodes) {
          client.generateProofOfElectionCodes(this.authenticateWith.electionCodes);
        } else if(this.authenticateWith.loginsClient) {
          const identityToken = await new LoginsApiClient(this.authenticateWith.loginsClient).requestIdentityToken();
          client.setIdentityToken(identityToken);
        }
        await client.expireVoterSessions(votingRoundToExpire.reference);
      } catch (e) {
        console.error(e)
      }
    },
  },
});
</script>
